import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageSquare, 
  Send, 
  Loader2,
  AlertCircle,
  Check,
  CheckCheck,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dbService, ConversationWithDetails, Message } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import { resolveStartupDisplayForViewer } from '../../lib/warmIntroSharing';

interface MessagesTabProps {
  selectedConversationId?: string | null;
  onConversationSelect?: (conversation: ConversationWithDetails) => void;
  onUnreadCountChange?: (conversationId: string, newCount: number) => void;
  isVerifyingAccess?: boolean;
  totalUnreadCount?: number; // Pass the total unread count from dashboard
}

// Interface for tracking read status
interface ReadStatus {
  [conversationId: string]: {
    [messageId: string]: boolean;
  };
}

const MessagesTab: React.FC<MessagesTabProps> = ({ 
  selectedConversationId, 
  onConversationSelect,
  onUnreadCountChange,
  isVerifyingAccess = false,
  totalUnreadCount: dashboardUnreadCount = 0
}) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithDetails | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [readStatus, setReadStatus] = useState<ReadStatus>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mobileMessagesContainerRef = useRef<HTMLDivElement>(null);
  const conversationsListRef = useRef<HTMLDivElement>(null);
  
  // Mobile state management
  const [showChatView, setShowChatView] = useState(false);
  
  // Note: Removed sentMessageIds tracking - now using processedMessageIds ref for better duplicate prevention

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Memoized function to prevent recreation on every render
  const loadConversations = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      if (!user?.id) return;

      const userConversations = await dbService.getUserConversations(user.id);

      setConversations(prev => {
        if (silent && prev.length > 0) {
          // Merge updates without wiping out local state
          const updatedConversations = prev.map(conv =>
            userConversations.find(c => c.id === conv.id) || conv
          );
          // Sort conversations by last message timestamp (most recent first)
          return updatedConversations.sort((a, b) => {
            const aTime = a.last_message?.created_at || a.updated_at;
            const bTime = b.last_message?.created_at || b.updated_at;
            return new Date(bTime).getTime() - new Date(aTime).getTime();
          });
        }
        // Sort conversations by last message timestamp (most recent first)
        return userConversations.sort((a, b) => {
          const aTime = a.last_message?.created_at || a.updated_at;
          const bTime = b.last_message?.created_at || b.updated_at;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
      });
    } catch (err) {
      console.error('Error loading conversations:', err);
      if (!silent) setError('Failed to load conversations. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user]);

  // Use a ref to track processed message IDs to prevent duplicates
  const processedMessageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Set up real-time subscription for messages
    if (selectedConversation && user) {
      const subscription = supabase
        .channel(`messages:${selectedConversation.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${selectedConversation.id}`,
          },
          async (payload) => {
            if (payload.eventType === 'INSERT') {
              const newMessage = payload.new as Message;
              
              // Check if we've already processed this message
              if (processedMessageIds.current.has(newMessage.id)) {
                console.log(`🚫 Message already processed, skipping: ${newMessage.id}`);
                return;
              }
              
              // Mark this message as processed
              processedMessageIds.current.add(newMessage.id);
              
              console.log(`📬 Processing new message: ${newMessage.id} (from ${newMessage.sender_id === user?.id ? 'self' : 'other'})`);
              
              // Add the message to UI
              setMessages(prev => {
                if (prev.some(msg => msg.id === newMessage.id)) {
                  console.log(`🚫 Message already in UI, skipping: ${newMessage.id}`);
                  return prev;
                }
                console.log(`✅ Adding message to UI: ${newMessage.id}`);
                return [...prev, newMessage];
              });
              
              // Update read status for the new message
              setReadStatus(prev => ({
                ...prev,
                [newMessage.conversation_id]: {
                  ...prev[newMessage.conversation_id],
                  [newMessage.id]: newMessage.is_read || false
                }
              }));

              // Mark messages as read if they're from the other user and we're in this conversation
              // Only mark as read if the message is not already read
              if (newMessage.sender_id !== user?.id && !newMessage.is_read) {
                console.log(`📖 Marking message as read: ${newMessage.id}`);
                await dbService.markMessageAsRead(newMessage.id);
                
                // Update the message in local state to show it's been read
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === newMessage.id ? { ...msg, is_read: true, read_at: new Date().toISOString() } : msg
                  )
                );

                // Update read status
                setReadStatus(prev => ({
                  ...prev,
                  [newMessage.conversation_id]: {
                    ...prev[newMessage.conversation_id],
                    [newMessage.id]: true
                  }
                }));

                // Update conversation with new message and sort by most recent activity
                setConversations(prev => {
                  const updatedConversations = prev.map(conv =>
                    conv.id === newMessage.conversation_id
                      ? {
                          ...conv,
                          unread_count: 0, // We're viewing it, so unread count is 0
                          last_message: newMessage,
                          updated_at: new Date().toISOString()
                        }
                      : conv
                  );
                  
                  // Sort conversations by last message timestamp (most recent first) - like WhatsApp
                  return updatedConversations.sort((a, b) => {
                    const aTime = a.last_message?.created_at || a.updated_at;
                    const bTime = b.last_message?.created_at || b.updated_at;
                    return new Date(bTime).getTime() - new Date(aTime).getTime();
                  });
                });

                // Notify parent component about unread count change
                if (onUnreadCountChange) {
                  onUnreadCountChange(newMessage.conversation_id, 0);
                }
              } else {
                // Message from current user - update conversation's last message
                setConversations(prev => {
                  const updatedConversations = prev.map(conv =>
                    conv.id === newMessage.conversation_id
                      ? {
                          ...conv,
                          last_message: newMessage,
                          updated_at: new Date().toISOString()
                        }
                      : conv
                  );
                  
                  // Sort conversations by last message timestamp (most recent first) - like WhatsApp
                  return updatedConversations.sort((a, b) => {
                    const aTime = a.last_message?.created_at || a.updated_at;
                    const bTime = b.last_message?.created_at || b.updated_at;
                    return new Date(bTime).getTime() - new Date(aTime).getTime();
                  });
                });
              }
            } else if (payload.eventType === 'UPDATE') {
              // Update message read status in real-time
              const updatedMessage = payload.new as Message;
              setMessages(prev => 
                prev.map(msg => 
                  msg.id === updatedMessage.id ? updatedMessage : msg
                )
              );

              // Update read status if message was marked as read
              if (updatedMessage.is_read) {
                setReadStatus(prev => ({
                  ...prev,
                  [updatedMessage.conversation_id]: {
                    ...prev[updatedMessage.conversation_id],
                    [updatedMessage.id]: true
                  }
                }));
              }
            }
          }
        )
        .subscribe();

             return () => {
         subscription.unsubscribe();
         // Clear processed message IDs when conversation changes
         const currentProcessedIds = processedMessageIds.current;
         currentProcessedIds.clear();
       };
    }
  }, [selectedConversation, user, onUnreadCountChange]);

  useEffect(() => {
    // Set up real-time subscription for conversations
    if (user) {
      const subscription = supabase
        .channel(`conversations:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: `startup_user_id=eq.${user.id},cdmo_user_id=eq.${user.id}`,
          },
          () => {
            // Silent refresh for conversations
            loadConversations(true);
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user, loadConversations]);

  // Subscription for messages from other conversations (not currently selected)
  useEffect(() => {
    if (user) {
      const subscription = supabase
        .channel(`all-messages:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
          },
          async (payload) => {
            const newMessage = payload.new as Message;
            
            // Skip if this is our own message
            if (newMessage.sender_id === user.id) {
              return;
            }
            
            // Skip if this is the currently selected conversation (handled by the other subscription)
            if (selectedConversation && newMessage.conversation_id === selectedConversation.id) {
              return;
            }
            
            // Check if this message is from one of our conversations
            const isOurConversation = conversations.some(conv => conv.id === newMessage.conversation_id);
            if (!isOurConversation) {
              return;
            }
            
            console.log(`📬 New message in other conversation: ${newMessage.id} (conversation: ${newMessage.conversation_id})`);
            
            // Update the conversation with new message and increment unread count
            setConversations(prev => {
              const updatedConversations = prev.map(conv =>
                conv.id === newMessage.conversation_id
                  ? {
                      ...conv,
                      unread_count: (conv.unread_count || 0) + 1, // Increment unread count
                      last_message: newMessage,
                      updated_at: new Date().toISOString()
                    }
                  : conv
              );
              
              // Sort conversations by last message timestamp (most recent first) - like WhatsApp
              const sortedConversations = updatedConversations.sort((a, b) => {
                const aTime = a.last_message?.created_at || a.updated_at;
                const bTime = b.last_message?.created_at || b.updated_at;
                return new Date(bTime).getTime() - new Date(aTime).getTime();
              });
              
              // Notify parent component about unread count change with the updated count
              if (onUnreadCountChange) {
                const updatedConversation = sortedConversations.find(conv => conv.id === newMessage.conversation_id);
                if (updatedConversation) {
                  onUnreadCountChange(newMessage.conversation_id, updatedConversation.unread_count || 0);
                }
              }
              
              return sortedConversations;
            });
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user, selectedConversation, conversations, onUnreadCountChange]);

  // Scroll to bottom function
  const scrollToBottom = useCallback((smooth = true) => {
    console.log("scrollToBottom called");
    
    // For desktop - scroll the messages container
    if (messagesContainerRef.current) {
      console.log("Scrolling desktop container");
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
    
    // For mobile - scroll the mobile messages container
    if (mobileMessagesContainerRef.current) {
      console.log("Scrolling mobile container");
      mobileMessagesContainerRef.current.scrollTo({
        top: mobileMessagesContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
    
    // Fallback - try scrollIntoView on the messages end ref
    if (messagesEndRef.current) {
      console.log("Fallback: scrollIntoView");
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  useEffect(() => {
    console.log("scroll useEffect triggered", { messagesCount: messages.length, conversationId: selectedConversation?.id });
    
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      scrollToBottom(true);
    }, 100);
  }, [messages, selectedConversation, scrollToBottom]);

  // Handle external conversation selection
  useEffect(() => {
    if (selectedConversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === selectedConversationId);
      if (conversation && !selectedConversation) {
        handleConversationSelect(conversation);
      }
    }
  }, [selectedConversationId, conversations, selectedConversation]);


  const loadMessages = async (conversationId: string) => {
    try {
      console.log(`=== LOADING MESSAGES ===`);
      console.log(`Conversation ID: ${conversationId}`);
      console.log(`Current User ID: ${user?.id}`);
      
      // Clear processed message IDs when loading new conversation
      processedMessageIds.current.clear();
      
      const conversationMessages = await dbService.getConversationMessages(conversationId);
      console.log(`📨 Loaded ${conversationMessages.length} messages`);
      
      setMessages(conversationMessages);
      
      // Initialize read status for this conversation
      const initialReadStatus: { [messageId: string]: boolean } = {};
      conversationMessages.forEach(msg => {
        initialReadStatus[msg.id] = msg.is_read || false;
      });
      
      setReadStatus(prev => ({
        ...prev,
        [conversationId]: initialReadStatus
      }));
      
      // Only mark messages as read if the current user is the RECEIVER of the messages
      // This prevents the sender from marking their own sent messages as "read"
      if (user?.id) {
        // Check if there are any unread messages from other users in this conversation
        const hasUnreadMessages = conversationMessages.some(msg => 
          msg.sender_id !== user.id && !msg.is_read
        );
        
        if (hasUnreadMessages) {
          console.log(`🎯 Marking unread messages from other users as read...`);
          await dbService.markMessagesAsRead(conversationId, user.id);
          
          // Update messages instantly - only mark messages from OTHER users as read
          setMessages(prev =>
            prev.map(msg =>
              msg.sender_id !== user.id && !msg.is_read
                ? { ...msg, is_read: true, read_at: new Date().toISOString() }
                : msg
            )
          );

          // Update read status for messages from other users only
          setReadStatus(prev => {
            const updatedStatus = { ...prev[conversationId] };
            conversationMessages.forEach(msg => {
              if (msg.sender_id !== user.id) {
                updatedStatus[msg.id] = true;
              }
            });
            
            return {
              ...prev,
              [conversationId]: updatedStatus
            };
          });

          // Update conversation unread count instantly and sort conversations
          setConversations(prev => {
            const updatedConversations = prev.map(conv =>
              conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
            );
            
            // Sort conversations by last message timestamp (most recent first)
            return updatedConversations.sort((a, b) => {
              const aTime = a.last_message?.created_at || a.updated_at;
              const bTime = b.last_message?.created_at || b.updated_at;
              return new Date(bTime).getTime() - new Date(aTime).getTime();
            });
          });

          // Notify parent component about unread count change
          if (onUnreadCountChange) {
            onUnreadCountChange(conversationId, 0);
          }
        } else {
          console.log(`📖 No unread messages from other users to mark as read`);
        }
      }
      
             console.log(`=== END LOADING MESSAGES ===`);
       
       // Scroll to bottom after loading messages (without animation for initial load)
       setTimeout(() => {
         scrollToBottom(false);
       }, 200);
       
     } catch (err) {
       console.error('❌ Error loading messages:', err);
     }
   };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user?.id) return;

    try {
      setSendingMessage(true);
      
      const sentMessage = await dbService.sendMessage(selectedConversation.id, user.id, newMessage);
      
      // Clear the input
      setNewMessage('');
      
      // DON'T add to local state here - let the real-time subscription handle it
      // This prevents duplicate messages from appearing
      
      // Update conversation's last message
      setConversations(prev => {
        const updatedConversations = prev.map(conv =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                last_message: sentMessage,
                updated_at: new Date().toISOString()
              }
            : conv
        );
        return updatedConversations.sort((a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      });

      // Note: No need to clean up message IDs - processedMessageIds ref handles this automatically
      
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleConversationSelect = useCallback((conversation: ConversationWithDetails) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
    
    // Show chat view on mobile
    setShowChatView(true);
    
    // Notify parent component
    if (onConversationSelect) {
      onConversationSelect(conversation);
    }
  }, [loadMessages, onConversationSelect]);

  const handleBackToConversations = () => {
    setShowChatView(false);
    // Don't clear selected conversation to maintain desktop behavior
  };

  // Helper function to check if a message is read
  const isMessageRead = (message: Message): boolean => {
    return readStatus[message.conversation_id]?.[message.id] || message.is_read || false;
  };

  const getStartupDisplay = (conversation: ConversationWithDetails) => {
    if (!user?.id) {
      return { companyName: 'Startup Company', contactName: '' };
    }
    return resolveStartupDisplayForViewer(conversation, user.id);
  };

  const getConversationName = (conversation: ConversationWithDetails) => {
    if (user?.id === conversation.startup_user_id) {
      return conversation.cdmo_applications?.company_name || 'CDMO Company';
    }
    return getStartupDisplay(conversation).companyName;
  };

  const getConversationContact = (conversation: ConversationWithDetails) => {
    if (user?.id === conversation.startup_user_id) {
      return conversation.cdmo_applications?.contact_name || 'CDMO Contact';
    }
    return getStartupDisplay(conversation).contactName;
  };

  const getConversationInitial = (conversation: ConversationWithDetails) => {
    const name = getConversationName(conversation);
    return name.charAt(0).toUpperCase();
  };

  const getMessageStatus = (message: Message) => {
    if (message.sender_id === user?.id) {
      const isRead = isMessageRead(message);
      
      if (message.read_at || isRead) {
        return (
          <div className="flex items-center space-x-1">
            <CheckCheck className="w-3 h-3 text-blue-500" />
            <span className="text-xs">Read</span>
          </div>
        );
      } else if (message.is_read) {
        return (
          <div className="flex items-center space-x-1">
            <CheckCheck className="w-3 h-3 text-gray-400" />
            <span className="text-xs">Delivered</span>
          </div>
        );
      } else {
        return (
          <div className="flex items-center space-x-1">
            <Check className="w-3 h-3 text-gray-400" />
            <span className="text-xs">Sent</span>
          </div>
        );
      }
    }
    return null;
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const formatConversationTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getLastMessagePreview = (conversation: ConversationWithDetails) => {
    if (conversation.last_message) {
      return conversation.last_message.content.length > 50 
        ? conversation.last_message.content.substring(0, 50) + '...'
        : conversation.last_message.content;
    }
    return "Click to view conversation...";
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-electric-blue animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => loadConversations()}
          className="px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Use the unread count from dashboard instead of calculating it locally
  const totalUnreadCount = dashboardUnreadCount;
  const activeConversationsCount = conversations.length;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 h-[600px] flex">
      {/* Desktop Layout: Always show both panels */}
      <div className="hidden md:flex w-full">
        {/* Left Panel - Conversation List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  Messages with Approved CDMOs
                  {totalUnreadCount > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                    </span>
                  )}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  {totalUnreadCount > 0 && (
                    <span className="text-sm text-blue-600 font-medium">{totalUnreadCount} unread messages</span>
                  )}
                  <span className="text-sm text-green-500">•</span>
                  <span className="text-sm text-gray-500">{activeConversationsCount} conversations</span>
                </div>
              </div>
            </div>
          </div>

          {/* Conversations List with Scrollbar */}
          <div 
            ref={conversationsListRef}
            className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          >
            {conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-2">
                  Start a conversation with an accepted CDMO request
                </p>
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => handleConversationSelect(conversation)}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                    selectedConversation?.id === conversation.id
                      ? 'bg-blue-50 border-blue-200'
                      : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg relative">
                      {getConversationInitial(conversation)}
                      {conversation.unread_count && conversation.unread_count > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">
                          {conversation.unread_count}
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900 truncate">{getConversationName(conversation)}</h4>
                        <span className="text-xs text-gray-500">{formatConversationTime(conversation.updated_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate mb-1">{getLastMessagePreview(conversation)}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
                          Approved
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Chat Area */}
        <div className="flex-1 flex flex-col">
          {isVerifyingAccess ? (
            // Show validation loading state on right side only
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-electric-blue animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Validating Access</h3>
                <p className="text-gray-500">Checking conversation permissions...</p>
              </div>
            </div>
          ) : selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {getConversationInitial(selectedConversation)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{getConversationName(selectedConversation)}</h3>
                      {getConversationContact(selectedConversation) ? (
                        <p className="text-sm text-gray-600">{getConversationContact(selectedConversation)}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
                      Approved
                    </span>
                    {/* {selectedConversation.unread_count && selectedConversation.unread_count > 0 && (
                      <span className="text-xs text-blue-600 font-medium">{selectedConversation.unread_count} unread</span>
                    )} */}
                  </div>
                </div>
              </div>

              {/* Messages with Scrollbar and Date Groups */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No messages yet</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Start the conversation by sending a message
                    </p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow-sm ${
                          message.sender_id === user?.id
                            ? 'bg-blue-500 text-white rounded-br-md'
                            : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                        }`}
                      >
                        <p className="text-sm leading-relaxed break-words whitespace-pre-line">{message.content}</p>
                        <div className={`flex items-center justify-end mt-1 space-x-1 ${
                          message.sender_id === user?.id ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          <span className="text-xs">
                            {formatMessageTime(message.created_at)}
                          </span>
                          {getMessageStatus(message)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-4 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">Select a Conversation</h3>
                <p className="text-gray-500">
                  Choose a conversation from the list to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout: Sliding Views */}
      <div className="md:hidden w-full relative overflow-hidden">
        {/* Conversations List View */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          showChatView ? '-translate-x-full' : 'translate-x-0'
        }`}>
          <div className="h-full flex flex-col">
            {/* Mobile Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                Messages
                {totalUnreadCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {activeConversationsCount} conversations
                {totalUnreadCount > 0 && (
                  <span className="text-blue-600 font-medium"> • {totalUnreadCount} unread</span>
                )}
              </p>
            </div>

            {/* Mobile Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No conversations yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Start a conversation with an accepted CDMO request
                  </p>
                </div>
              ) : (
                conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => handleConversationSelect(conversation)}
                    className="p-4 border-b border-gray-100 cursor-pointer active:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg relative flex-shrink-0">
                        {getConversationInitial(conversation)}
                        {conversation.unread_count && conversation.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">
                            {conversation.unread_count}
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900 truncate">{getConversationName(conversation)}</h4>
                          <span className="text-xs text-gray-500 flex-shrink-0">{formatConversationTime(conversation.updated_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 truncate mb-1">{getLastMessagePreview(conversation)}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
                            Approved
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Chat View */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          showChatView ? 'translate-x-0' : 'translate-x-full'
        }`}>
          {isVerifyingAccess ? (
            <div className="h-full flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-electric-blue animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Please Wait</h3>
                <p className="text-gray-500">Loading your conversion...</p>
              </div>
            </div>
          ) : selectedConversation ? (
            <div className="h-full flex flex-col">
              {/* Mobile Chat Header with Back Button */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleBackToConversations}
                    className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {getConversationInitial(selectedConversation)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{getConversationName(selectedConversation)}</h3>
                    {getConversationContact(selectedConversation) ? (
                      <p className="text-sm text-gray-600 truncate">{getConversationContact(selectedConversation)}</p>
                    ) : null}
                  </div>
                  <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full flex-shrink-0">
                    Approved
                  </span>
                </div>
              </div>

              {/* Mobile Messages with Date Groups */}
              <div 
                ref={mobileMessagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No messages yet</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Start the conversation by sending a message
                    </p>
                  </div>
                ) : (
                  messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs px-4 py-2 rounded-2xl shadow-sm ${
                              message.sender_id === user?.id
                                ? 'bg-blue-500 text-white rounded-br-md'
                                : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                            }`}
                          >
                            <p className="text-sm leading-relaxed">{message.content}</p>
                            <div className={`flex items-center justify-end mt-1 space-x-1 ${
                              message.sender_id === user?.id ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                              <span className="text-xs">
                                {formatMessageTime(message.created_at)}
                              </span>
                              {getMessageStatus(message)}
                            </div>
                          </div>
                        </div>
                      ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Mobile Message Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white text-sm"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-4 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-50">
              <div className="text-center p-8">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">No Conversation Selected</h3>
                <p className="text-gray-500">
                  Go back and choose a conversation to start messaging
                </p>
                <button
                  onClick={handleBackToConversations}
                  className="mt-4 px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors"
                >
                  Back to Conversations
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;
